import type { DesignTokens } from "./types";

/** CSS `font-family` stack for app + Figma spec (see `figmaPrimaryFontFamily` for Plugin API). */
export const typographyFontFamilyStack =
  '"greycliff-hebrew-cf", "Greycliff Hebrew CF", sans-serif';
const font = typographyFontFamilyStack;

/** `fontName.family` in Figma Plugin API — primary face inside `typographyFontFamilyStack`. */
export const figmaPrimaryFontFamily = "Greycliff Hebrew CF";

/**
 * Greycliff Hebrew CF style name for `figma.loadFontAsync` / `TextNode.fontName.style`
 * (Adobe Fonts naming in Figma).
 */
export function greycliffStyleForFontWeight(weight: number): string {
  if (weight <= 400) return "Regular";
  if (weight <= 500) return "Medium";
  if (weight <= 600) return "Demi Bold";
  if (weight <= 700) return "Bold";
  if (weight <= 800) return "Extra Bold";
  return "Heavy";
}

const defaultFontWeight = 600;

const colors: DesignTokens["colors"] & { secondaryLight: string } = {
  primary: "#1D2D50",
  secondary: "#96C5DF",
  secondaryLight: "#C0DCEC",
  pdfPrimary: "#101130",
  pdfAccent: "#628ca5",
  pdfDeepBlue: "#061456",
  pdfPurple: "#231f59",
  pdfMutedGray: "#a3a3a3",
  pdfLightAccent: "#C0DCEC",
  accent: "#B3C0C5",
  tooltipBackground: "#D5E8F2",
  background: "#ffffff",
  surface: "#F6F8FE",
  text: {
    primary: "#1D2D50",
    primaryPlaceholder: "#778196",
    secondary: "#628ca5", 
    tertiary: "#B3C0C5",
    inverse: "#F4F9FC",
  },
  border: "#87B1C9",
  error: "#e53e3e",
  success: "#00B80F",
  warning: "#d69e2e",
  chipGreen: "#E8F5E9",
  chipGray: "#EFEFEF",
  chipOrange: "#FFF3E0",
  inputBorder: "#002366",
  inputBorderFocus: "#001A4D",
};

/**
 * Rafael Cars — design tokens (Yissum-style structure).
 * Primary navy #101130, accent #a37a29 (aligned with admin UI).
 */
export const tokens: DesignTokens = {
  colors,
  buttonColors: {
    primary: "#1D2D50",
    secondary: "#96C5DF",
    secondaryLight: "#EEF6FA",
    accent: "#B3C0C5",
    outlinePrimary: "#1D2D50",
    outlineSecondary: "#96C5DF",
    outlineAccent: "#B3C0C5",
    primaryHover: "#0E182D",
    secondaryHover: "#87B1C9",
    secondaryLightHover: "#D5E8F2",
    outlineSecondaryHover: "#87B1C9",
    outlineSecondaryHoverBackground: "rgba(135, 177, 201, 0.2)",
    accentHover: "#E1E6E8",
    surfaceHover: "#D2D7E5",
  },

  typography: {
    a: {
      fontFamily: font,
      fontSize: "1.125rem",
      fontWeight: 800,
      lineHeight: 1.2,
    },
    aSmall: {
      fontFamily: font,
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h1: {
      fontFamily: font,
      fontSize: "2rem",
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontFamily: font,
      fontSize: "1.75rem",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontFamily: font,
      fontSize: "1.1rem",
      fontWeight: 600,
      lineHeight: 1.1,
    },
    h4: {
      fontFamily: font,
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h5: {
      fontFamily: font,
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: font,
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.45,
    },
    sectionTitle: {
      fontFamily: font,
      fontSize: "1.75rem",
      fontWeight: 600,
      lineHeight: 1.2,
    },
    carName: {
      fontFamily: font,
      fontSize: "1.75rem",
      fontWeight: 800,
      lineHeight: "1.625rem",
    },
    carDescription: {
      fontFamily: font,
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: "1.25rem",
    },
    filterButton: {
      fontFamily: font,
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: "1.25rem",
    },
    categoryLabel: {
      fontFamily: font,
      fontSize: "0.9375rem",
      fontWeight: 800,
      lineHeight: "normal",
      letterSpacing: "0.046875rem",
    },
    priceTitle: {
      fontFamily: font,
      fontSize: "1.5625rem",
      fontWeight: 600,
      lineHeight: "1.5rem",
      letterSpacing: "-0.03rem",
    },
    priceValue: {
      fontFamily: font,
      fontSize: "1.875rem",
      fontWeight: 600,
      lineHeight: "normal",
      letterSpacing: "-0.03rem",
    },
    netoImpact: {
      fontFamily: font,
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: "normal",
      letterSpacing: "-0.04875rem",
    },
    bodyLarge: {
      fontFamily: font,
      fontSize: "1.125rem",
      fontWeight: defaultFontWeight,
      lineHeight: 1.5,
    },
    body: {
      fontFamily: font,
      fontSize: "1.125rem",
      fontWeight: defaultFontWeight,
      lineHeight: 1.2,
    },
    bodySmall: {
      fontFamily: font,
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1,
    },
    caption: {
      fontFamily: font,
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    button: {
      fontFamily: font,
      fontSize: "1rem",
      fontWeight: 700,
      lineHeight: 1.5,
      letterSpacing: "0.01em",
    },
    buttonSmall: {
      fontFamily: font,
      fontSize: ".8rem",
      fontWeight: 700,
      lineHeight: 1.5,
      letterSpacing: "0.01em",
    },
    buttonXl: {
      fontFamily: font,
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: 1.5,
      letterSpacing: "0.01em",
    },
    label: {
      fontFamily: font,
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    tooltip: {
      fontFamily: font,
      fontSize: "0.6rem",
      fontWeight: 800,
      lineHeight: 1.5,
    },
    menuItem: {
      fontFamily: font,
      fontSize: ".9rem",
      fontWeight: 600,
      lineHeight: 1.2,
    },
    inputSmall: {
      fontFamily: font,
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.2,
    },
    inputMedium: {
      fontFamily: font,
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1,
    },
    inputLarge: {
      fontFamily: font,
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1,
    },
  },
  controls: {
    fieldHeight: 40,
  },
  spacing: {
    0: "0",
    1: "0.25rem",
    2: "0.5rem",
    3: "0.75rem",
    4: "1rem",
    6: "1.5rem",
    8: "2rem",
    10: "2.5rem",
    12: "3rem",
    14: "3.5rem",
    16: "4rem",
    20: "5rem",
    24: "6rem",
    32: "8rem",
    40: "10rem",
    48: "12rem",
    56: "14rem",
    64: "16rem",
    80: "20rem",
    96: "24rem",
    128: "32rem",
  },
  breakpoints: {
    xs: "0px",
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
  borderRadius: {
    none: "0",
    sm: "0.125rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    xxl: "20px",
    full: "9999px",
  },
  scroll: {
    width: "6px",
    thumb: colors.border,
    thumbHover: colors.secondary,
    track: "transparent",
  },
  transitions: {
    fast: "150ms ease-in-out",
    normal: "250ms ease-in-out",
    slow: "350ms ease-in-out",
  },
};

export default tokens;
